/**
 * Local-First Settings Component
 *
 * Allows users to configure local vs cloud service preferences
 * for each AI service (LLM, TTS, Translation, etc.).
 */

import React, { useCallback } from 'react';
import { Cloud, Server, RefreshCw, Info } from 'lucide-react';

import { useTranslation } from '../../hooks/useTranslation';
import { useLocalFirstConfig } from '../../hooks/useLocalFirstConfig';
import type { PreferenceMode, LocalFirstConfig } from '../../services/localFirstService';

// ============================================================================
// Types
// ============================================================================

interface ServiceConfigProps {
  serviceName: string;
  serviceKey: string;
  mode: PreferenceMode;
  fallbackEnabled: boolean;
  onUpdate: (mode: PreferenceMode, fallbackEnabled: boolean) => void;
  disabled?: boolean;
}

interface ModeOption {
  value: PreferenceMode;
  label: string;
  description: string;
  icon: React.ReactNode;
}

// ============================================================================
// ServiceConfig Sub-component
// ============================================================================

const ServiceConfig: React.FC<ServiceConfigProps> = ({
  serviceName,
  serviceKey,
  mode,
  fallbackEnabled,
  onUpdate,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const modeOptions: ModeOption[] = [
    {
      value: 'local_first',
      label: t('settings.localFirst.localFirst', { defaultValue: 'Local First' }),
      description: t('settings.localFirst.localFirstDesc', {
        defaultValue: 'Use local when available, fall back to cloud',
      }),
      icon: <Server className="h-4 w-4" />,
    },
    {
      value: 'local_only',
      label: t('settings.localFirst.localOnly', { defaultValue: 'Local Only' }),
      description: t('settings.localFirst.localOnlyDesc', {
        defaultValue: 'Never use cloud services',
      }),
      icon: <Server className="h-4 w-4" />,
    },
    {
      value: 'cloud_first',
      label: t('settings.localFirst.cloudFirst', { defaultValue: 'Cloud First' }),
      description: t('settings.localFirst.cloudFirstDesc', {
        defaultValue: 'Use cloud when available, fall back to local',
      }),
      icon: <Cloud className="h-4 w-4" />,
    },
    {
      value: 'cloud_only',
      label: t('settings.localFirst.cloudOnly', { defaultValue: 'Cloud Only' }),
      description: t('settings.localFirst.cloudOnlyDesc', {
        defaultValue: 'Never use local components',
      }),
      icon: <Cloud className="h-4 w-4" />,
    },
  ];

  const handleModeChange = useCallback(
    (newMode: PreferenceMode) => {
      onUpdate(newMode, fallbackEnabled);
    },
    [onUpdate, fallbackEnabled]
  );

  const handleFallbackChange = useCallback(
    (enabled: boolean) => {
      onUpdate(mode, enabled);
    },
    [onUpdate, mode]
  );

  const showFallbackOption = mode === 'local_first' || mode === 'cloud_first';

  return (
    <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 mb-4">
      <h4 className="font-medium text-secondary-900 dark:text-secondary-100 mb-3">{serviceName}</h4>

      <div className="space-y-3">
        {/* Mode Selection */}
        <div>
          <label htmlFor={`${serviceKey}-mode`} className="label-themed">
            {t('settings.localFirst.mode', { defaultValue: 'Mode' })}
          </label>
          <select
            id={`${serviceKey}-mode`}
            value={mode}
            onChange={(e) => handleModeChange(e.target.value as PreferenceMode)}
            disabled={disabled}
            className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {modeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
            {modeOptions.find((opt) => opt.value === mode)?.description}
          </p>
        </div>

        {/* Fallback Toggle */}
        {showFallbackOption && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={fallbackEnabled}
              onChange={(e) => handleFallbackChange(e.target.checked)}
              disabled={disabled}
              className="rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
            />
            <span className="text-sm text-secondary-700 dark:text-secondary-300">
              {t('settings.localFirst.enableFallback', {
                defaultValue: 'Enable automatic fallback',
              })}
            </span>
          </label>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const LocalFirstSettings: React.FC = () => {
  const { t } = useTranslation();
  const { config, loading, error, updatePreference, setAutoStart, refresh } = useLocalFirstConfig();

  // Service definitions
  const services = [
    { key: 'llm', name: t('settings.localFirst.llm', { defaultValue: 'Language Models (LLM)' }) },
    { key: 'tts', name: t('settings.localFirst.tts', { defaultValue: 'Text-to-Speech' }) },
    {
      key: 'translation',
      name: t('settings.localFirst.translation', { defaultValue: 'Translation' }),
    },
    {
      key: 'transcription',
      name: t('settings.localFirst.transcription', { defaultValue: 'Transcription' }),
    },
    {
      key: 'imageGeneration',
      name: t('settings.localFirst.imageGeneration', { defaultValue: 'Image Generation' }),
    },
  ];

  // Handle preference update with error handling
  const handlePreferenceUpdate = useCallback(
    async (service: string, mode: PreferenceMode, fallbackEnabled: boolean) => {
      try {
        await updatePreference(service, mode, fallbackEnabled);
      } catch (e) {
        console.error(`[LocalFirstSettings] Failed to update ${service}:`, e);
        // Toast notification would go here in a real implementation
      }
    },
    [updatePreference]
  );

  // Handle auto-start toggle
  const handleAutoStartChange = useCallback(
    async (enabled: boolean) => {
      try {
        await setAutoStart(enabled);
      } catch (e) {
        console.error('[LocalFirstSettings] Failed to update auto-start:', e);
      }
    },
    [setAutoStart]
  );

  // Loading state
  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-secondary-200 dark:bg-secondary-700 rounded-lg" />
        ))}
      </div>
    );
  }

  // Error state
  if (error || !config) {
    return (
      <div className="text-red-600 dark:text-red-400 p-4 rounded-lg bg-red-50 dark:bg-red-900/20">
        <div className="flex items-center gap-2 mb-2">
          <Info className="h-5 w-5" />
          <span className="font-medium">
            {error ||
              t('settings.localFirst.loadError', { defaultValue: 'Failed to load configuration' })}
          </span>
        </div>
        <button
          onClick={refresh}
          className="text-sm text-red-700 dark:text-red-300 hover:underline flex items-center gap-1"
        >
          <RefreshCw className="h-3 w-3" />
          {t('common.retry', { defaultValue: 'Retry' })}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-2 flex items-center gap-2">
          <Server className="h-5 w-5" />
          {t('settings.localFirst.title', { defaultValue: 'Local-First Settings' })}
        </h3>
        <p className="text-sm text-secondary-600 dark:text-secondary-400">
          {t('settings.localFirst.description', {
            defaultValue:
              'Configure how services choose between local components and cloud services.',
          })}
        </p>
      </div>

      {/* Auto-start Toggle */}
      <div className="mb-6 p-4 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg">
        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <span className="text-sm font-medium text-secondary-900 dark:text-secondary-100 block">
              {t('settings.localFirst.autoStart', { defaultValue: 'Auto-start components' })}
            </span>
            <span className="text-xs text-secondary-500 dark:text-secondary-400">
              {t('settings.localFirst.autoStartDesc', {
                defaultValue: 'Automatically start local components when needed',
              })}
            </span>
          </div>
          <button
            onClick={() => handleAutoStartChange(!config.autoStartComponents)}
            className={`relative w-11 h-6 rounded-full transition-colors ${
              config.autoStartComponents
                ? 'bg-primary-600'
                : 'bg-secondary-300 dark:bg-secondary-600'
            }`}
            role="switch"
            aria-checked={config.autoStartComponents}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                config.autoStartComponents ? 'translate-x-5' : ''
              }`}
            />
          </button>
        </label>
      </div>

      {/* Service Configurations */}
      {services.map((service) => {
        const pref = config[service.key as keyof LocalFirstConfig];
        // Type guard to ensure pref is ServicePreference
        if (typeof pref !== 'object' || !('mode' in pref)) return null;

        return (
          <ServiceConfig
            key={service.key}
            serviceName={service.name}
            serviceKey={service.key}
            mode={pref.mode}
            fallbackEnabled={pref.fallbackEnabled}
            onUpdate={(mode, fallbackEnabled) =>
              handlePreferenceUpdate(service.key, mode, fallbackEnabled)
            }
          />
        );
      })}
    </div>
  );
};

export default LocalFirstSettings;
